import { useMediaQuery } from '@mantine/hooks'
import dayjs from 'dayjs'
import _ from 'lodash'
import { useParams, usePathname, useRouter } from 'next/navigation'
import { ReactNode, useCallback, useMemo } from 'react'

import Footer from '@/features/common/components/Footer'
import Loading from '@/features/common/components/Loading'
import { ICurriculumNavList } from '@/features/common/types'
import { useAuth } from '@/shared/providers/Auth'
import { useCurriculum } from '@/shared/providers/Curriculum'
import { useInstitute } from '@/shared/providers/Institute'

import { DashboardIcon, NoteIcon, NoticeIcon, PenIcon, ProjectIcon, QnAIcon } from './Icons'
import MenubarHeader from './MenubarHeader'
import MenubarInfo from './MenubarInfo'
import MenubarNav from './MenubarNav'
import PcSidebar from './PcSidebar'
import { HomeIcon } from '@heroicons/react/24/outline'

interface IWithCurriculumLayout {
  children: ReactNode
  isLoading?: boolean
}

const WithCurriculumLayout = ({ children, isLoading = false }: IWithCurriculumLayout) => {
  const router = useRouter()
  const pathname = usePathname()
  const { curriculumId } = useParams() as { curriculumId: string }
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const { profile } = useAuth()
  const { instituteInfo } = useInstitute()
  const { curriculum } = useCurriculum()

  const gotoPage = useCallback((href: string) => router.push(href), [router])

  const isActive = (id: string) => {
    return id === 'board' ? pathname === `/${curriculumId}` : pathname.includes(id)
  }

  const isDetailPage = useMemo(
    () => pathname.split('/').filter(Boolean).length > 2 || pathname.includes('note'),
    [pathname],
  )

  const curriculumPeriod = useMemo(() => {
    const curDate = dayjs().startOf('day')
    if (curriculum) {
      const startDate = dayjs(curriculum.startedAt)
      const endDate = dayjs(curriculum.endedAt)

      if (curDate.isBefore(startDate)) {
        // 수강 시작일 전
        return '예정'
      } else if (curDate.isAfter(endDate)) {
        // 수강 종료일 후
        return '종료'
      } else {
        // 수강 중
        const remainingDays = endDate.diff(curDate, 'day')
        return remainingDays
      }
    }
  }, [curriculum])

  const CURRICULUM_NAV_LIST = [
    {
      id: 'board',
      icon: <DashboardIcon />,
      title: '강의보드',
      href: `/${curriculumId}`,
    },
    {
      id: 'notice',
      icon: <NoticeIcon />,
      title: '공지사항',
      href: `/${curriculumId}/notice`,
    },
    {
      id: 'note',
      icon: <NoteIcon />,
      title: 'AI 노트',
      href: `/${curriculumId}/note`,
    },
    {
      id: 'qna',
      icon: <QnAIcon />,
      title: 'Q&A',
      href: `/${curriculumId}/qna`,
    },
    // {
    // id: 'quiz',
    //   icon: <NoteCheckIcon />,
    //   title: '훈련평가',
    //   href: `/${curriculumId}/quiz`,
    // },
    {
      id: 'project',
      icon: <ProjectIcon />,
      title: '프로젝트',
      href: `/${curriculumId}/project`,
    },
    {
      id: 'survey',
      icon: <PenIcon />,
      title: '설문조사',
      href: `/${curriculumId}/survey`,
    },
  ]

  const lmsNavList = useMemo(() => {
    const defaultNav = CURRICULUM_NAV_LIST.find(item => item.id === 'board')
    const filteredNav = CURRICULUM_NAV_LIST.filter(item => curriculum?.lms?.includes(item.id))
    return [defaultNav, ...filteredNav] as ICurriculumNavList[]
  }, [curriculum])

  return (
    <div className="flex h-full min-h-screen flex-col">
      <section className={'flex flex-1 flex-col bg-alternative lg:flex-row'}>
        {isMobile ? (
          <>
            <MenubarHeader logoImg={instituteInfo?.logoImage} />
            <MenubarNav navList={lmsNavList} gotoPage={gotoPage} isActive={isActive} />
          </>
        ) : (
          <PcSidebar
            navList={lmsNavList}
            gotoPage={gotoPage}
            isActive={isActive}
            logoImg={instituteInfo?.logoImage}
            name={profile?.name}
            curriculumPeriod={curriculumPeriod}
          />
        )}

        <main
          className={`w-full ${isDetailPage ? 'bg-normal' : 'bg-alternative'} flex-1 px-4 py-10 md:px-6 md:py-10 lg:mx-auto lg:max-w-[1060px] lg:px-[42px] lg:py-16`}
        >
          {isLoading ? <Loading /> : <>{children}</>}
        </main>
      </section>
      <Footer />
    </div>
  )
}

export default WithCurriculumLayout
